import { useEffect, useState } from "react";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import { Send, Link2 } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric" }) : "");

// "Resend invite" for a client/staff login that has never signed in. Hidden once they have.
export function ResendInviteButton({ u, onDone }) {
  const [busy, setBusy] = useState(false);
  if (u.last_login_at || u.status === "disabled") return null;
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/users/${u.user_id}/resend-invite`);
      toast.success(`Invite re-sent to ${data.to} (valid 48 h)`); onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const n = u.invite_sent_count || 0;
  return (
    <button data-testid={`resend-invite-${u.user_id}`} onClick={send} disabled={busy}
      title={n ? `Invite sent ${n}×, last ${fmt(u.invite_last_sent_at)} — send a fresh 48 h link` : "Send a fresh 48 h set-password link"}
      className="h-8 inline-flex items-center gap-1.5 border border-amber-500 text-amber-800 px-2.5 text-xs hover:bg-amber-500 hover:text-white transition-colors disabled:opacity-50">
      <Send className="h-3.5 w-3.5" /> {busy ? "Sending…" : n ? `Resend invite (${n}×)` : "Resend invite"}
    </button>
  );
}

// Link a client Admin/MGT login to their own employee record (one login, two hats).
export function LinkEmployeeControl({ companyId, u, onDone }) {
  const [emps, setEmps] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { if (open && !emps) api.get(`/employees?company_id=${companyId}`).then((r) => setEmps(r.data)).catch(() => setEmps([])); }, [open, emps, companyId]);
  if (!["admin", "mgt"].includes(u.client_role || "admin")) return null;
  const set = async (employee_id) => {
    try {
      const { data } = await api.put(`/companies/${companyId}/users/${u.user_id}/link-employee`, { employee_id: employee_id || null });
      toast.success(employee_id ? `Linked to ${data.employee_name} — they can switch to "My pay" in the client portal` : "Employee link removed");
      setOpen(false); onDone?.();
    } catch (e) { toast.error(apiErr(e)); }
  };
  if (open) {
    return (
      <select data-testid={`link-employee-select-${u.user_id}`} autoFocus defaultValue={u.linked_employee_id || ""} onChange={(e) => set(e.target.value)} onBlur={() => setOpen(false)}
        className="h-8 border border-input bg-white px-2 text-xs">
        <option value="">— not linked —</option>
        {(emps || []).map((e) => <option key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name}{e.email && e.email.toLowerCase() === String(u.email).toLowerCase() ? " (same email)" : ""}</option>)}
      </select>
    );
  }
  return (
    <button data-testid={`link-employee-${u.user_id}`} onClick={() => setOpen(true)} title="Let this owner also see their own pay, T4s and ROEs as an employee — one login, two hats"
      className={`h-8 inline-flex items-center gap-1.5 border px-2.5 text-xs transition-colors ${u.linked_employee_id ? "border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white" : "border-border hover:border-black hover:bg-black hover:text-white"}`}>
      <Link2 className="h-3.5 w-3.5" /> {u.linked_employee_id ? "Employee linked" : "Link employee"}
    </button>
  );
}
